import { Box, Button } from "@mui/material";
import { disabledBlockSx } from "../disabledStyles";
import { ContentGalleryModal } from "@platformx/authoring-utilities";
import { ShowToastError } from "@platformx/utilities";
import { memo, useEffect, useState } from "react";
import { fetchSchemaWithData } from "../../../utils/helperFns";
import { getRepeaterValue, setRepeaterValue } from "../../../utils/repeaterPath";
import { DraggableItems } from "../DragableItem/DragableItems";
import HiddenTextBox from "../HiddenTextBox/HiddenTextBox";

// When rendered inside a (possibly nested) repeater, field.name is a synthetic
// RHF path (e.g. `entries[0].foo_entries_123`), not a real state key — reads and
// writes must go through field.repeaterPath/childName instead, mirroring the
// coordinate pattern builder.tsx's own handleChange/updateState helpers use.
const getCollectionValue = (state: any, field: any, isRepeater: boolean) =>
  isRepeater
    ? getRepeaterValue(state, field?.repeaterPath, field?.childName ?? "")
    : state?.[field?.name];

const resolveSchema = (output: any) =>
  output?.status === "fulfilled" ? output.value?.authoring_getSchemaContent || {} : {};

const setCollectionValue = (
  state: any,
  setState: any,
  field: any,
  isRepeater: boolean,
  value: any,
) => {
  if (isRepeater) {
    setState(setRepeaterValue(state, field?.repeaterPath, field?.childName ?? "", value));
    return;
  }
  setState({
    ...state,
    [field?.name]: value,
  });
};

const AddCollection = ({
  state,
  setState,
  field,
  error,
  register,
  clearErrors,
  isRepeater = false,
  isDisabled = false,
}: any) => {
  const [isOpenContent, setIsOpenContent] = useState(false);

  interface PopulatedItem {
    title: string;
    name: string;
    tag_name: string;
    current_page_url: string;
  }

  const [populatedItems, setPopulatedItems] = useState<PopulatedItem[]>([]);
  const collectionValue = getCollectionValue(state, field, isRepeater);

  const handleGalleryClose = () => {
    setIsOpenContent(false);
  };

  const handleSelectedContent = (items) => {
    const newArray = items?.slots?.map((val) => {
      return {
        Title: val.Title,
        content_path: val.path || val.Path || val.content_path,
        content_name: val.page || val.content_name,
        CurrentPageURL: val.slug || val.Slug || val.CurrentPageURL,
        content_type: val.ContentType || val.content_type,
        is_hidden: false,
        type: "collection",
      };
    });
    setCollectionValue(state, setState, field, isRepeater, newArray);
  };

  //GetSchemaData API
  const getSchema = async (Items: any) => {
    try {
      const promises = Items?.map((item: any) => {
        return fetchSchemaWithData(item.content_type, item.content_name, item.type);
      });
      const outputs: any = await Promise.allSettled(promises);
      const fallbackContentType = field?.allowed_collection?.[0];
      if (fallbackContentType) {
        await Promise.all(
          outputs.map(async (output: any, index: number) => {
            const item = Items?.[index];
            if (resolveSchema(output)?.title || item?.content_type === fallbackContentType) return;
            const retryOutput = await fetchSchemaWithData(
              fallbackContentType,
              item?.content_name,
              item?.type,
            ).then(
              (value) => ({ status: "fulfilled", value }),
              (reason) => ({ status: "rejected", reason }),
            );
            if (resolveSchema(retryOutput)?.title) {
              outputs[index] = retryOutput;
            }
          }),
        );
      }

      const tempArray: PopulatedItem[] = [];
      outputs?.forEach((output: any, index: number) => {
        const item = Items?.[index];
        if (output?.status === "rejected") {
          console.error("Error in fetching row items data", output);
        }
        const schema = resolveSchema(output);
        tempArray.push({
          title: schema?.title || item?.Title,
          name: schema?.name || item?.content_name,
          tag_name: schema?.tag_name,
          current_page_url: schema?.current_page_url || item?.CurrentPageURL,
        });
      });
      setPopulatedItems(tempArray);
    } catch (err: any) {
      ShowToastError("Error in fetching row items data");
    }
  };

  useEffect(() => {
    clearErrors(field?.name);
    if (collectionValue?.length > 0) {
      getSchema(collectionValue);
    } else if (collectionValue?.length === 0) {
      setPopulatedItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionValue]);

  // Adapter so the shared DraggableItems component (which reads/writes `state[name]`
  // directly as a flat array) can drive the repeater-nested value here.
  const draggableAdapterState = { [field?.name]: collectionValue || [] };
  const draggableAdapterSetState = (adapterState: any) => {
    setCollectionValue(state, setState, field, isRepeater, adapterState?.[field?.name] ?? []);
  };

  return (
    <Box id={`${field?.name}_id`} sx={disabledBlockSx(isDisabled)}>
      <Box
        sx={{
          ".draggable-category": {
            paddingRight: "0",
          },
        }}>
        <DraggableItems
          state={draggableAdapterState}
          setState={draggableAdapterSetState}
          name={field?.name}
          populatedItems={populatedItems}
        />
      </Box>
      <Button variant='primaryButton' onClick={() => setIsOpenContent(true)}>
        {field?.title || "Choose Collection"}
      </Button>
      {isOpenContent ? (
        <ContentGalleryModal
          isDialogOpen={isOpenContent}
          onClose={handleGalleryClose}
          handleSelectedContent={handleSelectedContent}
          selectedItem={collectionValue || []}
          multi_select={field?.multiple}
          single_select={!field?.multiple}
          name={field?.name}
          allowed_content={field?.allowed_content}
          allowed_collection={field?.allowed_collection}
        />
      ) : null}
      <HiddenTextBox
        error={error}
        value={collectionValue?.length || ""}
        register={register}
        name={field?.name}
        required={!collectionValue?.length ? field?.validations?.[0]?.message : ""}
      />
    </Box>
  );
};

export default memo(AddCollection);
